import { useEffect, useCallback, useRef } from "react";
import { mainBus, metricsBus } from "@/common/buses";
import { useLocation } from "react-router-dom";
import { useAppStore } from "@/common/store/appStore";
import { useBodyLock } from "@/common/hooks/useBodyLock";
import { useScrollStore } from "@/common/store/scrollStore";
import { useVideoQueueStore } from "@/common/store/videoQueueStore";
import { useBoostedGifStore } from "@/common/store/boostedGifStore";

type CheckableVisibilityElement = HTMLElement & {
  checkVisibility?: () => boolean;
};

const isElementVisible = (element: CheckableVisibilityElement) => {
  const visibilityFn = element.checkVisibility;

  if (typeof visibilityFn === "function") {
    try {
      return visibilityFn.call(element);
    } catch {
      return false;
    }
  }

  return false;
};

export const useFullScreen = () => {
  const isFullScreen = useAppStore((state) => state.isFullScreen);
  const toggleFullScreen = useAppStore((state) => state.toggleFullScreen);
  const scrollElement = useScrollStore((state) => state.scrollElement);
  const location = useLocation();
  const locationKey = location.key;
  const getLastVisibleVideo = useVideoQueueStore((state) => state.getLastVisibleVideo);
  const videoQueue = useVideoQueueStore((s) => s.videoQueue);
  const boostedGifsData = useBoostedGifStore((state) => state.gifs);

  const fallbackFeedItemRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isFullScreen) {
      return;
    }

    const lastKnownGif = getLastVisibleVideo();

    if (typeof lastKnownGif !== "string") {
      return;
    }

    if (boostedGifsData.find(gif => gif.id === lastKnownGif)) {
      return;
    }

    fallbackFeedItemRef.current = lastKnownGif;
  }, [videoQueue, isFullScreen, boostedGifsData]);

  const findFeedItemElement = useCallback((feedItemId: string | null) => {
    if (!feedItemId) {
      return null;
    }

    const selector = `[data-feed-item-id="${feedItemId}"]`;

    for (const element of document.querySelectorAll<CheckableVisibilityElement>(selector)) {
      if (isElementVisible(element)) {
        return element;
      }
    }

    return null;
  }, []);

  const getActiveFeedItemId = useCallback(() => {
    const centerX = window.innerWidth / 2;
    const centerY = window.innerHeight / 2;
    const elementAtCenter = document.elementFromPoint(centerX, centerY);

    if (!elementAtCenter || typeof elementAtCenter.closest !== "function") {
      return undefined;
    }

    const moduleElement = elementAtCenter.closest("[data-feed-item-id]") as HTMLElement;

    if (!moduleElement) {
      return undefined;
    }

    return moduleElement.dataset.feedItemId ?? undefined;
  }, []);

  const scrollIntoView = useCallback((feedItem?: string) => {
    if (!feedItem) {
      scrollElement?.scrollTo({ top: 0 });
      return;
    }

    const performScroll = () => {
      const element = findFeedItemElement(feedItem);

      if (element) {
        element.scrollIntoView({ behavior: "auto", block: "center" });
        return;
      }

      if (fallbackFeedItemRef.current) {
        const fallbackElement = findFeedItemElement(fallbackFeedItemRef.current);

        if (fallbackElement) {
          fallbackElement.scrollIntoView({ behavior: "auto", block: "center" });
          return;
        }
      }

      scrollElement?.scrollTo({ top: 0 });
    };

    if (typeof window !== "undefined" && typeof window.requestAnimationFrame === "function") {
      window.requestAnimationFrame(performScroll);
      return;
    }

    setTimeout(performScroll, 0);
  }, [findFeedItemElement, scrollElement]);

  useEffect(() => {
    if (!isFullScreen) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      const parent = document.querySelector(".previewFeed");

      if (!parent) {
        return;
      }

      switch (event.key) {
      case "Escape":
        event.preventDefault();
        mainBus.emit("toggle_fullscreen");
        break;

      case "ArrowDown":
        event.preventDefault();
        parent.scrollBy({
          top: window.innerHeight,
          behavior: "smooth",
        });
        break;

      case "ArrowUp":
        event.preventDefault();
        parent.scrollBy({
          top: -window.innerHeight,
          behavior: "smooth",
        });
        break;

      default:
        break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [findFeedItemElement, isFullScreen]);

  useEffect(() => {
    return () => {
      toggleFullScreen(false);
    };
  }, [toggleFullScreen, locationKey]);

  const toggleFullscreen = useCallback((payload?: { id?: string }) => {
    const targetId = payload?.id ?? getActiveFeedItemId();

    if (isFullScreen) {
      toggleFullScreen(false);
      metricsBus.emit("forceFullscreenExited");
    } else {
      toggleFullScreen(!isFullScreen);
    }

    scrollIntoView(targetId);
  }, [
    isFullScreen,
    toggleFullScreen,
    scrollIntoView,
    getActiveFeedItemId
  ]);

  useEffect(() => {
    mainBus.on("toggle_fullscreen", toggleFullscreen);

    return () => {
      mainBus.off("toggle_fullscreen", toggleFullscreen);
    };
  }, [toggleFullscreen]);

  useBodyLock(isFullScreen);
};
